import type * as api from '@gitbook/api-client';
import { type NavigationScreenState, resolveURLToNavigation } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import type { SidebarTreeItemValue } from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import {
    resolvePageTarget,
    SiteContentIcon,
    SiteExternalLinkIcon,
    SiteSectionGroupIcon,
    SiteSectionIcon,
    SpaceIcon,
} from '../../Components';
import { SiteVariantIcon } from '../../Components/Site/SiteVariantIcon';
import type { NavigationTarget } from '../../navigation';
import { getSiteSectionsFromSiteSectionGroup } from './structure';

export type AnySiteItem = SiteItem | SiteSectionItem | SiteSectionGroupItem | SiteSpaceItem;

export type AnySiteManagementItem = AnySiteItem | SiteExternalLinkItem;

interface SiteItem extends SidebarTreeItemValue {
    type: 'site';
    site: api.Site;
    href: NavigationTarget;
    isActive: (screen: NavigationScreenState) => boolean;
    children: SiteStructureItem[];
}

interface SiteTreeContext {
    hideEmptySectionGroups?: boolean;
    expandableSiteSectionIds?: string[];
    includeExternalLinks?: boolean;
    revisions?: ReadonlyMap<string, api.Revision>;
}

/**
 * Get a site item from a site and its structure.
 */
export function getSiteTreeItem(
    input: {
        site: api.Site;
        structure: api.SiteStructure;
    },
    context?: SiteTreeContext
): SiteItem {
    const { site, structure } = input;
    const screen = resolveURLToNavigation(site.urls.app);
    assert(screen, 'Impossible to resolve site URL');
    return {
        id: site.id,
        type: 'site',
        title: site.title,
        icon: <SiteContentIcon site={site} />,
        site,
        href: { screen },
        isActive: (screen) => screen.type === 'site' && screen.site === site.id,
        children: getSiteStructureItems(structure, context ?? {}) as SiteStructureItem[],
    };
}

export type SiteStructureItem = SiteSectionItem | SiteSectionGroupItem | SiteSpaceItem;

export type SiteManagementStructureItem = SiteStructureItem | SiteExternalLinkItem;

function getSiteStructureItems(
    structure: api.SiteStructure,
    context: SiteTreeContext
): SiteManagementStructureItem[] {
    switch (structure.type) {
        case 'sections': {
            return structure.structure
                .map((item) => {
                    switch (item.object) {
                        case 'site-section':
                            return getSiteSectionItem(item, context);
                        case 'site-section-group': {
                            if (
                                context.hideEmptySectionGroups &&
                                getSiteSectionsFromSiteSectionGroup(item).length === 0
                            ) {
                                return null;
                            }
                            return getSiteSectionGroupItem(item, context);
                        }
                        case 'site-external-link':
                            return context.includeExternalLinks
                                ? getSiteExternalLinkItem(item)
                                : null;
                        default:
                            assertNever(item);
                    }
                })
                .filter((item) => item !== null);
        }
        case 'siteSpaces': {
            return structure.structure.map((siteSpace) => getSiteSpaceItem(siteSpace, context));
        }
        default:
            assertNever(structure);
    }
}

export interface SiteSectionGroupItem extends SidebarTreeItemValue {
    type: 'site-section-group';
    siteSectionGroup: api.SiteSectionGroup;
    children: Array<SiteSectionGroupItem | SiteSectionItem>;
}

function getSiteSectionGroupItem(
    siteSectionGroup: api.SiteSectionGroup,
    context: SiteTreeContext
): SiteSectionGroupItem {
    return {
        id: siteSectionGroup.id,
        type: 'site-section-group',
        title: siteSectionGroup.title,
        icon: <SiteSectionGroupIcon siteSectionGroup={siteSectionGroup} />,
        siteSectionGroup,
        children: siteSectionGroup.children.flatMap((child) => {
            switch (child.object) {
                case 'site-section-group':
                    return getSiteSectionGroupItem(child, context);
                case 'site-section':
                    return getSiteSectionItem(child, context);
                case 'site-external-link':
                    return context.includeExternalLinks ? getSiteExternalLinkItem(child) : [];
                default:
                    return assertNever(child);
            }
        }) as Array<SiteSectionGroupItem | SiteSectionItem>,
    };
}

export interface SiteExternalLinkItem extends SidebarTreeItemValue {
    type: 'site-external-link';
    siteExternalLink: api.SiteExternalLink;
    description: string | null;
    url: string;
}

function getSiteExternalLinkItem(siteExternalLink: api.SiteExternalLink): SiteExternalLinkItem {
    return {
        id: siteExternalLink.id,
        type: 'site-external-link',
        title: siteExternalLink.title,
        icon: <SiteExternalLinkIcon siteExternalLink={siteExternalLink} />,
        siteExternalLink,
        description: siteExternalLink.description ?? null,
        url: siteExternalLink.url,
    };
}

export interface SiteSectionItem extends SidebarTreeItemValue {
    type: 'site-section';
    siteSection: api.SiteSection;
    href: NavigationTarget | undefined;
    isActive: ((screen: NavigationScreenState) => boolean) | undefined;
    children: SiteSpaceItem[];
    space: api.Space | null;
    url: string | null;
}

function getSiteSectionItem(
    siteSection: api.SiteSection,
    context: SiteTreeContext
): SiteSectionItem {
    const singleSiteSpace =
        siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : undefined;
    const isExpandable = context?.expandableSiteSectionIds?.includes(siteSection.id);
    return {
        id: siteSection.id,
        type: 'site-section',
        title: siteSection.title,
        icon: <SiteSectionIcon siteSection={siteSection} />,
        siteSection,
        href: singleSiteSpace ? getSiteSpaceHref(singleSiteSpace, context.revisions) : undefined,
        isActive: singleSiteSpace ? getSiteSpaceIsActive(singleSiteSpace) : undefined,
        children:
            isExpandable !== undefined
                ? isExpandable
                    ? siteSection.siteSpaces.map((space) => getSiteSpaceItem(space, context))
                    : []
                : singleSiteSpace
                  ? []
                  : siteSection.siteSpaces.map((space) => getSiteSpaceItem(space, context)),
        space: singleSiteSpace ? singleSiteSpace.space : null,
        url: siteSection.urls.published || null,
    };
}

export interface SiteSpaceItem extends SidebarTreeItemValue {
    type: 'site-space';
    siteSpace: api.SiteSpace;
    href: NavigationTarget;
    isActive: (screen: NavigationScreenState) => boolean;
    space: api.Space;
    url: string | null;
}

function getSiteSpaceItem(siteSpace: api.SiteSpace, context: SiteTreeContext): SiteSpaceItem {
    return {
        id: siteSpace.id,
        type: 'site-space',
        title: siteSpace.title,
        siteSpace,
        href: getSiteSpaceHref(siteSpace, context.revisions),
        isActive: getSiteSpaceIsActive(siteSpace),
        icon: SiteVariantIcon,
        space: siteSpace.space,
        url: siteSpace.urls.published || null,
    };
}

export function getSiteSpaceHref(
    siteSpace: api.SiteSpace,
    revisions: ReadonlyMap<string, api.Revision> | undefined
): NavigationTarget {
    const pageTarget = siteSpace.pageId
        ? resolvePageTarget(revisions?.get(siteSpace.space.id), siteSpace.pageId)
        : null;
    const screen = resolveURLToNavigation(pageTarget?.urls.app ?? siteSpace.space.urls.app);
    assert(screen !== null, 'Site section screen should be defined');
    return { screen };
}

function getSiteSpaceIsActive(siteSpace: api.SiteSpace) {
    return (screen: NavigationScreenState) =>
        screen.type === 'space' && screen.space === siteSpace.space.id;
}
export type ContentItem = SpaceItem | CollectionItem;

export function getContentItems(
    contents: (api.OrganizationAllCollection | api.OrganizationAllSpace)[]
): ContentItem[] {
    const spaces: SpaceItem[] = [];
    const collections: CollectionItem[] = [];
    for (const content of contents) {
        if ('collection' in content) {
            collections.push(getCollectionItem(content));
        } else {
            spaces.push(getSpaceItem(content));
        }
    }
    return [...spaces, ...collections];
}

interface SpaceItem extends SidebarTreeItemValue {
    type: 'space';
    space: api.OrganizationAllSpace;
    href: NavigationTarget;
    isActive: (screen: NavigationScreenState) => boolean;
}

function getSpaceItem(space: api.OrganizationAllSpace): SpaceItem {
    return {
        id: space.space.id,
        type: 'space',
        title: space.space.title,
        space,
        href: getSpaceHref(space.space),
        isActive: getSpaceIsActive(space.space),
        icon: <SpaceIcon space={space.space} />,
    };
}

export interface CollectionItem extends SidebarTreeItemValue {
    type: 'collection';
    collection: api.OrganizationAllCollection;
    children: ContentItem[];
}

function getCollectionItem(collection: api.OrganizationAllCollection): CollectionItem {
    return {
        id: collection.collection.id,
        type: 'collection',
        title: collection.collection.title,
        collection,
        icon: Icon.Collection,
        children: getContentItems(collection.content),
    };
}

function getSpaceHref(space: api.Space): NavigationTarget {
    const screen = resolveURLToNavigation(space.urls.app);
    assert(screen !== null, 'Space screen should be defined');
    return { screen };
}

function getSpaceIsActive(space: api.Space) {
    return (screen: NavigationScreenState) => screen.type === 'space' && screen.space === space.id;
}
